import React, { useState, useMemo, useRef, useEffect, Suspense } from 'react';
import { Loader2, Heart, User, Info, ExternalLink, Menu, Flag } from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import ReactMarkdown from 'react-markdown';
import { useVegAIAnalyze } from './hooks/useVegAIAnalyze';
import { useHistory } from './hooks/useHistory';
import { useFavorites } from './hooks/useFavorites';
import { ProductInputPanel } from './flows/ProductInputPanel';
import { ManualInputPanel } from './flows/ManualInputPanel';
import { HistorySidebar } from './components/HistorySidebar';
import { FeedbackWidget } from './components/FeedbackWidget';
import { FavoritesModal } from './components/FavoritesModal';
import { LaunchInfoModal } from './components/LaunchInfoModal';
import { AccountModal } from './components/AccountModal';
import { ImportLocalDataModal } from './components/ImportLocalDataModal';
import { AdminPanel } from './components/AdminPanel';
import { FoodProfilePanel } from './components/FoodProfilePanel';
import { ForYouCard } from './components/ForYouCard';
import { PrimaryTabBar } from './components/PrimaryTabBar';
import {
  clearServerCacheAPI,
  deleteAccountAPI,
  flagReviewQueueAPI,
  getAccountExportAPI,
  trackAnalyticsEventAPI,
} from './utils/api';
import { formatProductLabel, formatIngredientDisplay, normalizeShoutingInText } from './utils/formatDisplayLabel';
import { buildContextualProductInput } from './utils/productRerun';
import { useAccount } from './auth/AccountContext';
import { useAccountData } from './hooks/useAccountData';
import { useFoodProfile } from './hooks/useFoodProfile';

// html5-qrcode is heavy and can choke some mobile WebViews — load it only after
// the shell mounts so a parse/runtime failure there does not blank the whole UI.
const ScannerInputPanel = React.lazy(() =>
  import('./flows/ScannerInputPanel').then((m) => ({ default: m.ScannerInputPanel }))
);

const FLAG_REVIEW_REASONS = [
  { value: 'verdict', label: 'Vegan status' },
  { value: 'product', label: 'Product match' },
  { value: 'ingredients', label: 'Ingredients' },
  { value: 'variant', label: 'Flavor or variant' },
  { value: 'allergen', label: 'Allergen' },
  { value: 'alternatives', label: 'Alternatives' },
  { value: 'source', label: 'Source / trust' },
  { value: 'other', label: 'Other' },
];

const flagReasonLabel = (value) =>
  FLAG_REVIEW_REASONS.find((reason) => reason.value === value)?.label || '';

// Add/Update CSS styles for logo and header
const logoStyles = `
.logo {
  height: 48px;
  max-height: 48px;
  width: auto;
  flex-shrink: 0;
  display: block;
}
.vegai-logo-mark {
  height: 74px;
  width: auto;
  display: block;
  object-fit: contain;
}
.vegai-logo-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}
.header-bar {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px 12px;
  padding: 8px 12px 10px;
  background-color: rgba(255,255,255,0.88);
  box-shadow: none;
}
.header-logo-wrap {
  justify-self: center;
  min-width: 0;
  display: flex;
  justify-content: center;
}
.nav-tabs {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  padding: 2px 0 4px;
}
.nav-tabs::-webkit-scrollbar {
  height: 5px;
}
.nav-tabs::-webkit-scrollbar-thumb {
  background: #bbf7d0;
  border-radius: 999px;
}
.header-history-slot {
  display: flex;
  align-items: center;
}
@media (max-width: 900px) {
  .logo {
    height: 44px;
    max-height: 44px;
  }
  .vegai-logo-mark {
    height: 68px;
  }
}
@media (max-width: 600px) {
  .header-bar {
    grid-template-columns: 1fr;
    justify-items: stretch;
    padding: 8px 10px;
    gap: 10px;
  }
  .header-history-slot {
    width: 100%;
  }
  .header-logo-wrap {
    justify-self: center;
  }
  .nav-tabs {
    width: 100%;
    max-width: 100%;
    justify-content: flex-start;
  }
  .logo {
    height: 38px;
    max-height: 38px;
  }
  .vegai-logo-mark {
    height: 60px;
  }
}
`;

function Header({
  activeTab,
  setActiveTab,
  historyHidden,
  historyCount = 0,
  onShowHistory,
  onHideHistory,
  accountUser,
  onOpenAccount,
}) {
  const menuButton = (
    <button
      type="button"
      onClick={historyHidden ? onShowHistory : onHideHistory}
      className="inline-flex h-11 w-11 items-center justify-center rounded-2xl text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
      aria-label={historyHidden ? `Show history${historyCount ? `, ${historyCount} items` : ''}` : 'Hide history'}
      title={historyHidden ? 'Show history' : 'Hide history'}
    >
      <Menu className="h-5 w-5" />
    </button>
  );
  const accountButton = (
    <button
      type="button"
      onClick={onOpenAccount}
      className={`relative inline-flex h-11 w-11 items-center justify-center rounded-2xl transition-colors ${
        accountUser
          ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
          : 'text-slate-700 hover:bg-emerald-50 hover:text-emerald-700'
      }`}
      aria-label={accountUser ? 'Open your VegAI account' : 'Sign in to VegAI'}
      title={accountUser ? 'Your account' : 'Sign in'}
    >
      <User className="h-5 w-5" />
      {accountUser && (
        <span
          className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-emerald-600 ring-2 ring-white"
          aria-hidden="true"
        />
      )}
    </button>
  );

  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-100 bg-[rgba(255,255,255,0.92)] backdrop-blur-xl" style={{ boxShadow: 'none' }}>
      <style>{logoStyles}</style>

      {/* ── Desktop (md+) ── */}
      <div className="hidden h-[76px] w-full items-center px-6 md:grid md:grid-cols-[1fr_auto_1fr]">
        <div className="justify-self-start">
          {menuButton}
        </div>
        <button
          type="button"
          onClick={() => setActiveTab('scan')}
          className="vegai-logo-button justify-self-center"
          aria-label="Go to VegAI scan"
        >
          <img src={`${process.env.PUBLIC_URL}/vegai-logo-transparent.png`} alt="VegAI" className="vegai-logo-mark" />
        </button>
        <div className="justify-self-end">{accountButton}</div>
      </div>

      {/* ── Mobile ── */}
      <div className="flex flex-col gap-3 p-3 md:hidden">
        <div className="grid grid-cols-[1fr_auto_1fr] items-center">
          <div className="justify-self-start">{menuButton}</div>
          <button
            type="button"
            onClick={() => setActiveTab('scan')}
            className="vegai-logo-button justify-self-center"
            aria-label="Go to VegAI scan"
          >
            <img src={`${process.env.PUBLIC_URL}/vegai-logo-transparent.png`} alt="VegAI" className="vegai-logo-mark" />
          </button>
          <div className="justify-self-end">{accountButton}</div>
        </div>
      </div>

      <PrimaryTabBar activeTab={activeTab} onChange={setActiveTab} />
    </header>
  );
}

const getSourceDomain = (url) => { // eslint-disable-line no-unused-vars
  try {
    return new URL(String(url || '')).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
};

const isValidHttpSourceUrl = (url) => {
  try {
    const u = new URL(String(url || '').trim());
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
};

// eslint-disable-next-line no-unused-vars
const plantBasedConfirmations = [
  "No animal ingredients showed up. Good to go!",
  "Label looks clean on our end. Snack on!",
  "We scanned hard. Nothing weird. You're clear to proceed!",
  "All plant-friendly, no surprises. Safe to eat!",
  "Our AI double-checked. You're good to go!",
  "Everything checked out smoothly. Enjoy with confidence!",
  "No flags, no fuss. You're all set!",
  "This one passed the test. Dig in!",
  "Quiet scan, solid result. You're good to go!",
  "The label behaved itself. Feel free to enjoy!"
];

// --- LOCKED ENDGAME ALLERGEN DESCRIPTIONS ---
const ALLERGEN_DESCRIPTIONS = {
  milk: "Milk is a common dairy ingredient and a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  soy: "Soy is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  egg: "Egg is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  eggs: "Eggs are a major allergen. They can cause severe allergic reactions, including anaphylaxis.",
  wheat: "Wheat is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  peanut: "Peanut is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  peanuts: "Peanuts are a major allergen. They can cause severe allergic reactions, including anaphylaxis.",
  tree_nut: "Tree nuts are a major allergen. They can cause severe allergic reactions, including anaphylaxis.",
  "tree nuts": "Tree nuts are a major allergen. They can cause severe allergic reactions, including anaphylaxis.",
  fish: "Fish is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  shellfish: "Shellfish is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  sesame: "Sesame is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  tofu: "Tofu is made from soy, which is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  whey: "Whey is made from milk, which is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  casein: "Casein is made from milk, which is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  yogurt: "Yogurt is made from milk, which is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  cheese: "Cheese is made from milk, which is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  butter: "Butter is made from milk, which is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  cream: "Cream is made from milk, which is a major allergen. It can cause severe allergic reactions, including anaphylaxis.",
  tartrazine: "Tartrazine is a synthetic food dye. It may cause allergic reactions in some individuals.",
  annatto: "Annatto is a natural food coloring. It may cause allergic reactions in some individuals.",
  carmine: "Carmine is a red pigment from insects. It may cause allergic reactions in some individuals.",
  gelatin: "Gelatin is a protein from animal collagen. It may cause allergic reactions in some individuals.",
  sulfite: "Sulfites are preservatives. They may cause allergic reactions in some individuals.",
  sulfites: "Sulfites are preservatives. They may cause allergic reactions in some individuals.",
  msg: "MSG is a flavor enhancer. It may cause allergic reactions in some individuals.",
  mustard: "Mustard is a condiment. It may cause allergic reactions in some individuals.",
  celery: "Celery is a vegetable. It may cause allergic reactions in some individuals.",
  barley: "Barley contains gluten. People with celiac disease or gluten sensitivity should avoid it.",
  rye: "Rye contains gluten. People with celiac disease or gluten sensitivity should avoid it.",
  oats: "Oats may contain gluten due to cross-contamination during processing. People with celiac disease should look for certified gluten-free oats.",
  malt: "Malt is typically derived from barley and contains gluten. People with celiac disease or gluten sensitivity should avoid it.",
  lupin: "Lupin is a legume. It may cause allergic reactions in some individuals.",
  mollusk: "Mollusk is a shellfish. It may cause allergic reactions in some individuals."
};
// --- END LOCKED ALLERGEN DESCRIPTIONS ---


// Add at the top of the file, after imports:
const getVitaminNote = (ingredient, classification) => {
  const lower = ingredient.toLowerCase();

  // Only show tooltip for known non-vegan vitamins
  const nonVeganVitamins = ["vitamin d3", "vitamin a palmitate"];

  if (
    nonVeganVitamins.includes(lower) &&
    (classification === "nonPlantBasedIngredients" || classification === "questionableIngredients")
  ) {
    return "This vitamin is typically derived from animal sources unless stated otherwise. It's not considered vegan.";
  }

  return null;
};

// Add this utility function near the top (after imports, before components):
function mapStatusToDisplay(status) {
  // Handle new EndGame format
  if (status === "vegan") return "Vegan";
  if (status === "not-vegan") return "Not Vegan";
  if (status === "maybe-vegan") return "Possibly Not Vegan";
  
  // Handle legacy format (for backwards compatibility)
  if (status === "Plant-Based") return "Vegan";
  if (status === "Not Plant-Based") return "Not Vegan";
  if (status === "Maybe Plant-Based") return "Possibly Not Vegan";
  
  return status; // fallback
}

function normalizeStatus(s = '') {
  const t = String(s).toLowerCase().trim();
  if (t.includes('not-vegan') || t.includes('not plant') || t.includes('not vegan') || t.includes('non-plant') || t.includes('animal')) return 'not-vegan';
  // Use 'maybe-vegan' to stay aligned with the backend status string and the
  // `maybe-vegan` checks that drive UI rendering elsewhere in this file.
  if (t.includes('maybe') || t.includes('questionable') || t.includes('unclear') || t.includes('unknown')) return 'maybe-vegan';
  if (t.includes('vegan') || t.includes('plant-based') || t.includes('plant based')) return 'vegan';
  if (t === 'false' || t === '0' || t === 'no') return 'not-vegan';
  if (t === 'true' || t === '1' || t === 'yes') return 'vegan';
  return 'unknown';
}

function cleanResultText(value) {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') {
    const clean = String(value).trim();
    return clean.toLowerCase() === '[object object]' ? '' : clean;
  }
  if (Array.isArray(value)) return value.map(cleanResultText).filter(Boolean).join(' ').trim();
  if (typeof value === 'object') {
    return cleanResultText(
      value.productMatch || value.productName || value.name || value.label || value.title || value.query || value.input || value.barcode
    );
  }
  return String(value).trim();
}

function extractDetectedAllergens(arr) {
  if (!Array.isArray(arr)) return [];

  const truthy = (v) => v === true || v === 'true' || v === 1;
  const nameOf = (o) => {
    if (typeof o === 'string') return o;
    if (o && typeof o === 'object') {
      return o.name || o.label || o.ingredient || o.tag || o.id || o.allergen || o[Object.keys(o)[0]] || 'Unknown';
    }
    return String(o).replace('[object Object]', 'Unknown');
  };

  return arr
    .map(a => {
      if (typeof a === 'string') return { name: a.trim(), detected: true };
      if (a && typeof a === 'object') {
        const detected =
          truthy(a.detected) || truthy(a.present) || truthy(a.found) || truthy(a.isPresent) ||
          (typeof a.confidence === 'number' && a.confidence >= 0.5) ||
          (typeof a.score === 'number' && a.score >= 0.5) ||
          (!('detected' in a) && !('present' in a) && !('found' in a) && !('isPresent' in a));
        return { name: nameOf(a), detected };
      }
      return { name: nameOf(a), detected: true };
    })
    .filter(Boolean)
    .filter(x => x.detected && x.name && x.name !== 'Unknown')
    .map(x => x.name);
}

function isAdminUiBuildFlagEnabled() {
  const v = process.env.REACT_APP_ENABLE_ADMIN_UI;
  if (v == null || String(v).trim() === '') return false;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === '1' || s === 'yes';
}

function userRequestedAdminView() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '';
  const hash = (window.location.hash || '').toLowerCase();
  if (path === '/admin' || path.endsWith('/admin')) return true;
  // Plain hash or HashRouter-style #/admin
  if (hash === '#admin' || hash === '#/admin' || hash.startsWith('#/admin/')) return true;
  try {
    const qs = new URLSearchParams(window.location.search || '');
    if (qs.get('vegai_admin') === '1' || qs.get('admin') === '1') return true;
  } catch (_) {
    /* ignore */
  }
  return false;
}

export default function VegAI() {
  const account = useAccount();
  const [adminRouteTick, setAdminRouteTick] = useState(0);
  useEffect(() => {
    const onHash = () => setAdminRouteTick((n) => n + 1);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  void adminRouteTick;
  const wantsAdmin = userRequestedAdminView();
  const adminUiEnabled = isAdminUiBuildFlagEnabled();
  const isAdminView = adminUiEnabled && wantsAdmin;
  const [activeTab, setActiveTab] = useState('scan');
  const [usesDesktopResultZones, setUsesDesktopResultZones] = useState(false);
  const handlePrimaryTabChange = React.useCallback((nextTab) => {
    setActiveTab(nextTab);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    const updateDesktopResultZones = () => setUsesDesktopResultZones(mediaQuery.matches);
    updateDesktopResultZones();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateDesktopResultZones);
      return () => mediaQuery.removeEventListener('change', updateDesktopResultZones);
    }

    mediaQuery.addListener(updateDesktopResultZones);
    return () => mediaQuery.removeListener(updateDesktopResultZones);
  }, []);
  const [openAllergens, setOpenAllergens] = useState(new Set());
  
  // Manual input state
  const [manualInput, setManualInput] = useState(''); // eslint-disable-line no-unused-vars
  const [manualResult, setManualResult] = useState(null);
  const [isAnalyzingManual, setIsAnalyzingManual] = useState(false);
  // Ref to the analysis-result panel; used to auto-scroll the result into
  // view on mobile so users don't have to scroll past the input form.
  const resultsPanelRef = useRef(null);
  const [manualError, setManualError] = useState(null); // eslint-disable-line no-unused-vars
  
  // Product input state
  const [productInput, setProductInput] = useState(''); // eslint-disable-line no-unused-vars
  const [productResult, setProductResult] = useState(null);
  const [isAnalyzingProduct, setIsAnalyzingProduct] = useState(false);
  const [productError, setProductError] = useState(null); // eslint-disable-line no-unused-vars
  // eslint-disable-next-line no-unused-vars
  const [supportiveMessage, setSupportiveMessage] = useState("");
  
  // Scanner input state
  const [scannerInput, setScannerInput] = useState(''); // eslint-disable-line no-unused-vars
  const [scannerResult, setScannerResult] = useState(null);
  const [isAnalyzingScanner, setIsAnalyzingScanner] = useState(false);
  const [scannerError, setScannerError] = useState(null); // eslint-disable-line no-unused-vars
  const [isScannedIngredientsDialogOpen, setIsScannedIngredientsDialogOpen] = useState(false);
  const [showDesktopScrollHint, setShowDesktopScrollHint] = useState(false);
  const hasAnyResultsPanel = !!(
    manualResult ||
    productResult ||
    scannerResult ||
    isAnalyzingManual ||
    isAnalyzingProduct ||
    isAnalyzingScanner
  );
  const isAnyAnalysisRunning = isAnalyzingManual || isAnalyzingProduct || isAnalyzingScanner;
  const latestActiveResult = manualResult || productResult || scannerResult;
  const hasAnyCompletedResult = !!(latestActiveResult && !isAnyAnalysisRunning);
  // Removed isScanning - now using isAnalyzingScanner

  // Auto-scroll the results panel into view on mobile whenever a new
  // analysis run starts or finishes. Desktop (>=768px) keeps its
  // side-by-side layout so no scroll is needed.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    if (!isMobile) return;
    const hasResult = !!(manualResult || productResult || scannerResult);
    const isLoading = isAnalyzingManual || isAnalyzingProduct || isAnalyzingScanner;
    if (!hasResult && !isLoading) return;
    if (!resultsPanelRef.current) return;
    // Defer one frame so the panel has mounted/painted before scrolling.
    const id = window.requestAnimationFrame(() => {
      resultsPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    return () => window.cancelAnimationFrame(id);
  }, [
    manualResult,
    productResult,
    scannerResult,
    isAnalyzingManual,
    isAnalyzingProduct,
    isAnalyzingScanner,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (!usesDesktopResultZones || !hasAnyResultsPanel || isAnyAnalysisRunning) {
      setShowDesktopScrollHint(false);
      return undefined;
    }

    const updateHint = () => {
      const doc = document.documentElement;
      const body = document.body;
      const scrollHeight = Math.max(doc.scrollHeight, body?.scrollHeight || 0);
      const hasOverflow = scrollHeight > window.innerHeight + 24;
      setShowDesktopScrollHint(hasOverflow && window.scrollY < 24);
    };

    const frame = window.requestAnimationFrame(updateHint);
    const timers = [
      window.setTimeout(updateHint, 150),
      window.setTimeout(updateHint, 500),
    ];
    const resizeObserver = typeof ResizeObserver === 'function'
      ? new ResizeObserver(updateHint)
      : null;
    resizeObserver?.observe(document.body);
    window.addEventListener('scroll', updateHint, { passive: true });
    window.addEventListener('wheel', updateHint, { passive: true });
    window.addEventListener('touchmove', updateHint, { passive: true });
    window.addEventListener('resize', updateHint);

    return () => {
      window.cancelAnimationFrame(frame);
      timers.forEach((timer) => window.clearTimeout(timer));
      resizeObserver?.disconnect();
      window.removeEventListener('scroll', updateHint);
      window.removeEventListener('wheel', updateHint);
      window.removeEventListener('touchmove', updateHint);
      window.removeEventListener('resize', updateHint);
    };
  }, [usesDesktopResultZones, hasAnyResultsPanel, hasAnyCompletedResult, isAnyAnalysisRunning, latestActiveResult]);
  
  // Toggle function for allergen info boxes - now supports multiple open tooltips
  const toggleAllergen = (name) => {
    setOpenAllergens(prev => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };
  
  // Reset keys for clearing panel inputs
  const [manualResetKey, setManualResetKey] = React.useState(0);
  const [productResetKey, setProductResetKey] = React.useState(0);
  const [scannerResetKey, setScannerResetKey] = React.useState(0);

  // Ref to the Product Name input — used by the scanner panel to pivot a
  // BARCODE_NOT_FOUND miss into a name-based search without leaving the page.
  const productInputRef = React.useRef(null);

  const foodProfile = useFoodProfile({
    user: account.user,
    session: account.session,
  });

  // Hook instances for separated flows
  const manual = useVegAIAnalyze('manual', { foodProfile: foodProfile.profile });
  const product = useVegAIAnalyze('product', { foodProfile: foodProfile.profile });
  const scanner = useVegAIAnalyze('scanner', { foodProfile: foodProfile.profile });

  // History functionality
  const localHistory = useHistory();
  const localFavorites = useFavorites();
  const accountData = useAccountData({
    user: account.user,
    session: account.session,
    localFavorites: localFavorites.items,
    localHistory: localHistory.items,
  });
  const history = account.user ? accountData.history : localHistory;
  const favorites = account.user ? accountData.favorites : localFavorites;
  const addHistoryEntry = history.add;
  const combinedImportOffer = useMemo(() => {
    if (!accountData.importOffer && !foodProfile.importOffer) return null;
    return {
      favorites: accountData.importOffer?.favorites || 0,
      history: accountData.importOffer?.history || 0,
      foodProfile: Boolean(foodProfile.importOffer),
    };
  }, [accountData.importOffer, foodProfile.importOffer]);
  const handleImportDeviceData = React.useCallback(async () => {
    if (accountData.importOffer) await accountData.importLocalData();
    if (foodProfile.importOffer) await foodProfile.importLocalProfile();
  }, [accountData, foodProfile]);
  const handleKeepDeviceDataSeparate = React.useCallback(() => {
    if (accountData.importOffer) accountData.dismissImport();
    if (foodProfile.importOffer) foodProfile.dismissImport();
  }, [accountData, foodProfile]);
  
  // Track inputs for history
  const [lastManualInput, setLastManualInput] = React.useState('');
  const [lastProductInput, setLastProductInput] = React.useState('');
  const [lastScannerInput, setLastScannerInput] = React.useState('');
  
  // Rerun state
  const [rerunId, setRerunId] = React.useState(null);
  const [isRerunning, setIsRerunning] = React.useState(false);
  const rerunCancelledRef = React.useRef(false);
  const rerunEntryRef = React.useRef(null);
  const scannerHistoryPromptedRef = React.useRef(new WeakSet());
  const [isHistoryHidden, setIsHistoryHidden] = React.useState(true);

  const [favoritesOpen, setFavoritesOpen] = React.useState(false);
  const [accountOpen, setAccountOpen] = React.useState(false);
  const [launchInfoPage, setLaunchInfoPage] = React.useState(null);
  const [favoriteFeedback, setFavoriteFeedback] = React.useState(null);
  // Tracks the exact variant object (with fdcId/offCode) for the most recent variant selection
  // so re-run can look up the exact same DB record rather than re-searching by label string.
  const [lastProductVariantLookup, setLastProductVariantLookup] = React.useState(null);
  /** { variant: 'success'|'error', message: string } after clear-cache action */
  const [serverCacheFeedback, setServerCacheFeedback] = React.useState(null);
  const [flagReviewFeedback, setFlagReviewFeedback] = React.useState(null);
  const [flagReviewDialogOpen, setFlagReviewDialogOpen] = React.useState(false);
  const [flagReviewReason, setFlagReviewReason] = React.useState('');
  const [flagReviewOtherText, setFlagReviewOtherText] = React.useState('');

  useEffect(() => {
    if (isAdminView) return;
    trackAnalyticsEventAPI({
      eventName: 'page_view',
      feature: 'app',
      metadata: { path: window.location.pathname || '/' },
    });
  }, [isAdminView]);

  useEffect(() => {
    if (isAdminView) return;
    trackAnalyticsEventAPI({
      eventName: 'tab_view',
      feature: activeTab === 'scan' ? 'scanner' : 'navigation',
      metadata: { path: activeTab },
    });
  }, [activeTab, isAdminView]);

  // Sync hook results to existing state variables (to preserve current result rendering)
  // Don't update results while re-run is in progress to keep loading screen visible
  // Use functional updates so we never clobber a just-applied re-run result when the hook
  // state has not flushed yet (product.result can briefly stay null one frame after await).
  React.useEffect(() => {
    if (rerunId != null) return;
    setManualResult((prev) => {
      const next = manual.result;
      if (next == null && prev != null) return prev;
      return next;
    });
  }, [manual.result, rerunId]);
  React.useEffect(() => {
    if (rerunId != null) return;
    setProductResult((prev) => {
      const next = product.result;
      if (next == null && prev != null) return prev;
      return next;
    });
  }, [product.result, rerunId]);
  React.useEffect(() => {
    if (rerunId != null) return;
    setScannerResult((prev) => {
      const next = scanner.result;
      if (next == null && prev != null) return prev;
      return next;
    });
  }, [scanner.result, rerunId]);

  // Sync loading and error states to existing setters
  // Show loading state during re-run or actual API loading
  React.useEffect(() => { 
    setIsAnalyzingManual(manual.isLoading || (rerunId !== null)); 
  }, [manual.isLoading, rerunId]);
  React.useEffect(() => { 
    if (!rerunId) setManualError(manual.error); 
  }, [manual.error, rerunId]);

  React.useEffect(() => { 
    setIsAnalyzingProduct(product.isLoading || (rerunId !== null)); 
  }, [product.isLoading, rerunId]);
  React.useEffect(() => { 
    if (!rerunId) setProductError(product.error); 
  }, [product.error, rerunId]);

  React.useEffect(() => { 
    setIsAnalyzingScanner(scanner.isLoading || (rerunId !== null)); 
  }, [scanner.isLoading, rerunId]);
  React.useEffect(() => { 
    if (!rerunId) setScannerError(scanner.error); 
  }, [scanner.error, rerunId]);

  // Capture successful results in history (skip during re-runs)
  React.useEffect(() => {
    if (manual.result && lastManualInput && !rerunId) {
      const detectedAllergens = extractDetectedAllergens(manual.result.allergens);
      
      addHistoryEntry({
        mode: 'manual',
        input: lastManualInput,
        status: normalizeStatus(manual.result.status),
        confidence: manual.result.confidence,
        productMatch: manual.result.productMatch,
        nonPlantBasedIngredients: manual.result.nonPlantBasedIngredients,
        questionableIngredients: manual.result.questionableIngredients,
        allergens: detectedAllergens,
      });
    }
  }, [manual.result, lastManualInput, rerunId, addHistoryEntry]);

  React.useEffect(() => {
    if (product.result && lastProductInput && !rerunId) {
      const detectedAllergens = extractDetectedAllergens(product.result.allergens);
      
      addHistoryEntry({
        mode: 'product',
        input: lastProductInput,
        variantLookup: lastProductVariantLookup || undefined,
        status: normalizeStatus(product.result.status),
        confidence: product.result.confidence,
        productMatch: product.result.productMatch,
        nonPlantBasedIngredients: product.result.nonPlantBasedIngredients,
        questionableIngredients: product.result.questionableIngredients,
        allergens: detectedAllergens,
      });
    }
  }, [product.result, lastProductInput, lastProductVariantLookup, rerunId, addHistoryEntry]);

  React.useEffect(() => {
    if (scanner.result && lastScannerInput && !rerunId) {
      const detectedAllergens = extractDetectedAllergens(scanner.result.allergens);
      const scannedIngredients = Array.isArray(scanner.result.scannedIngredients)
        ? scanner.result.scannedIngredients.map((x) => String(x || '').trim()).filter(Boolean)
        : [];
      let historyLabel = '';
      if (
        scannedIngredients.length > 0 &&
        scanner.result &&
        !scannerHistoryPromptedRef.current.has(scanner.result) &&
        typeof window !== 'undefined' &&
        typeof window.prompt === 'function'
      ) {
        scannerHistoryPromptedRef.current.add(scanner.result);
        historyLabel = String(
          window.prompt('Optional: name this ingredient scan for History', '') || ''
        ).trim().slice(0, 80);
      }
      
      addHistoryEntry({
        mode: 'scanner',
        input: lastScannerInput,
        historyLabel: historyLabel || undefined,
        rerunInput: scannedIngredients.length > 0 ? {
          type: 'scannedIngredients',
          ingredients: scannedIngredients,
          productMatch: scanner.result.productMatch || undefined,
          sources: scanner.result.sources,
          resolvedVia: scanner.result.resolvedVia,
        } : undefined,
        status: normalizeStatus(scanner.result.status),
        confidence: scanner.result.confidence,
        productMatch: scanner.result.productMatch,
        nonPlantBasedIngredients: scanner.result.nonPlantBasedIngredients,
        questionableIngredients: scanner.result.questionableIngredients,
        allergens: detectedAllergens,
      });
    }
  }, [scanner.result, lastScannerInput, rerunId, addHistoryEntry]);

  // Reset handlers that mirror current behavior AND bump the key
  const resetManual = () => {
    manual.reset();
    setManualResult(null);
    setManualError(null);
    setIsAnalyzingManual(false);
    setManualResetKey(k => k + 1);
  };

  const resetProduct = () => {
    product.reset();
    setProductResult(null);
    setProductError(null);
    setIsAnalyzingProduct(false);
    setProductResetKey(k => k + 1);
  };

  const cancelRerun = () => {
    rerunCancelledRef.current = true;
    const entry = rerunEntryRef.current;
    if (entry) {
      history.add({
        mode: entry.mode || 'manual',
        input: entry.input || '',
        status: 'cancelled',
        confidence: 0,
        cancelled: true,
        variantLookup: entry.variantLookup,
      });
    }
    resetManual();
    resetProduct();
    resetScanner();
    setManualResult(null);
    setProductResult(null);
    setScannerResult(null);
    setIsRerunning(false);
    setRerunId(null);
    rerunEntryRef.current = null;
  };

  const cancelProduct = () => {
    if (lastProductInput) {
      history.add({
        mode: 'product',
        input: lastProductInput,
        status: 'cancelled',
        confidence: 0,
        cancelled: true,
        // So re-run can call analyzeVariant(fdcId/offCode) instead of re-searching by name (multi-brand).
        variantLookup: lastProductVariantLookup || undefined,
      });
    }
    resetProduct();
  };

  const resetScanner = () => {
    scanner.reset();
    setScannerResult(null);
    setScannerError(null);
    setIsAnalyzingScanner(false);
    setScannerResetKey(k => k + 1);
  };

  // Called from ScannerInputPanel when a BARCODE_NOT_FOUND error is shown and
  // the user clicks "Search by name". Clears the scanner state, then focuses
  // and scrolls the Product Name input into view so they can type immediately.
  const pivotToProductSearch = React.useCallback((_barcode) => {
    resetScanner();
    setTimeout(() => {
      const el = productInputRef.current;
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (_) { /* older browsers */ }
      el.focus();
    }, 0);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const resetAll = () => {
    setFlagReviewFeedback(null);
    resetManual();
    resetProduct();
    resetScanner();
  };

  const openFlagReviewDialog = React.useCallback(() => {
    const live = productResult || scannerResult || manualResult;
    if (!live) {
      setFlagReviewFeedback({ type: 'error', text: 'Nothing to flag yet — run a search or analysis first.' });
      return;
    }
    if (!live.status) {
      setFlagReviewFeedback({ type: 'error', text: 'Result is still loading. Try again in a moment.' });
      return;
    }
    setFlagReviewFeedback(null);
    setFlagReviewDialogOpen(true);
  }, [productResult, scannerResult, manualResult]);

  const closeFlagReviewDialog = React.useCallback(() => {
    setFlagReviewDialogOpen(false);
    setFlagReviewReason('');
    setFlagReviewOtherText('');
  }, []);

  const handleFlagForReview = React.useCallback(async () => {
    const live = productResult || scannerResult || manualResult;
    if (!live) {
      setFlagReviewFeedback({ type: 'error', text: 'Nothing to flag yet — run a search or analysis first.' });
      setFlagReviewDialogOpen(false);
      return;
    }
    if (!live.status) {
      setFlagReviewFeedback({ type: 'error', text: 'Result is still loading. Try again in a moment.' });
      setFlagReviewDialogOpen(false);
      return;
    }
    const selectedReasonLabel = flagReasonLabel(flagReviewReason);
    const otherText = flagReviewOtherText.trim();
    if (!selectedReasonLabel) {
      setFlagReviewFeedback({ type: 'error', text: 'Choose a reason before sending this for review.' });
      return;
    }
    if (flagReviewReason === 'other' && !otherText) {
      setFlagReviewFeedback({ type: 'error', text: 'Add a short note for Other before sending this for review.' });
      return;
    }

    let query = '';
    const productMatch = live.productMatch || undefined;
    if (productResult) {
      query = (lastProductInput || '').trim() || productMatch || '';
    } else if (scannerResult) {
      query = cleanResultText(lastScannerInput) || productMatch || 'Ingredient label scan';
    } else {
      query = (lastManualInput || '').trim() || productMatch || '';
    }
    if (!query) {
      setFlagReviewFeedback({ type: 'error', text: 'Nothing to flag — run a search or analysis first.' });
      return;
    }

    setFlagReviewFeedback(null);
    try {
      await flagReviewQueueAPI({
        query,
        productMatch,
        reason: 'user_flag',
        result: {
          status: live.status,
          confidence: live.confidence,
          productMatch: live.productMatch,
          summary: typeof live.summary === 'string' ? live.summary.slice(0, 600) : undefined,
          path: productResult ? 'product' : scannerResult ? 'scanner' : 'manual',
          userFlag: {
            reason: flagReviewReason,
            label: selectedReasonLabel,
            detail: flagReviewReason === 'other' ? otherText.slice(0, 300) : undefined,
          },
        },
      });
      setFlagReviewReason('');
      setFlagReviewOtherText('');
      setFlagReviewDialogOpen(false);
      setFlagReviewFeedback({ type: 'success', text: 'Thanks — we’ll review this.' });
      window.setTimeout(() => setFlagReviewFeedback(null), 5000);
    } catch (err) {
      setFlagReviewFeedback({ type: 'error', text: err?.message || 'Could not send flag. Try again.' });
    }
  }, [
    productResult,
    scannerResult,
    manualResult,
    lastProductInput,
    lastScannerInput,
    lastManualInput,
    flagReviewReason,
    flagReviewOtherText,
  ]);

  const activeFavoriteEntry = React.useMemo(() => {
    const live = productResult || scannerResult || manualResult;
    if (!live || !live.status) return null;

    const mode = productResult ? 'product' : scannerResult ? 'scanner' : 'manual';
    const input = mode === 'product'
      ? ((lastProductInput || live.productMatch || '').trim())
      : mode === 'scanner'
        ? (cleanResultText(lastScannerInput) || live.productMatch || 'Scan result')
        : ((lastManualInput || live.productMatch || '').trim());
    if (!input && !live.productMatch) return null;

    const scannedIngredients = Array.isArray(live.scannedIngredients)
      ? live.scannedIngredients.map((x) => String(x || '').trim()).filter(Boolean)
      : [];

    return {
      mode,
      input: input || live.productMatch || 'Saved result',
      favoriteLabel: live.productMatch || undefined,
      variantLookup: mode === 'product' ? (lastProductVariantLookup || undefined) : undefined,
      rerunInput: mode === 'scanner' && scannedIngredients.length > 0 ? {
        type: 'scannedIngredients',
        ingredients: scannedIngredients,
        productMatch: live.productMatch || undefined,
        sources: live.sources,
        resolvedVia: live.resolvedVia,
      } : undefined,
      status: normalizeStatus(live.status),
      confidence: live.confidence,
      productMatch: live.productMatch,
      nonPlantBasedIngredients: live.nonPlantBasedIngredients,
      questionableIngredients: live.questionableIngredients,
      allergens: extractDetectedAllergens(live.allergens),
    };
  }, [
    productResult,
    scannerResult,
    manualResult,
    lastProductInput,
    lastScannerInput,
    lastManualInput,
    lastProductVariantLookup,
  ]);

  const isActiveFavoriteSaved = favorites.isSaved(activeFavoriteEntry);

  const handleSaveFavorite = React.useCallback(() => {
    if (!activeFavoriteEntry) {
      setFavoriteFeedback({ type: 'error', text: 'Run a check before saving a favorite.' });
      return;
    }
    const wasSaved = favorites.isSaved(activeFavoriteEntry);
    favorites.toggle(activeFavoriteEntry);
    trackAnalyticsEventAPI({
      eventName: 'favorite_toggle',
      feature: 'favorites',
      metadata: {
        mode: activeFavoriteEntry.mode || 'unknown',
        action: wasSaved ? 'remove' : 'save',
      },
    });
    setFavoriteFeedback({
      type: 'success',
      text: wasSaved ? 'Removed from favorites.' : 'Saved to favorites.',
    });
    window.setTimeout(() => setFavoriteFeedback(null), 3500);
  }, [activeFavoriteEntry, favorites]);

  const handleFavoriteHistoryEntry = React.useCallback((entry) => {
    favorites.toggle({
      ...entry,
      favoriteLabel: entry.historyLabel || entry.productMatch || entry.input || undefined,
    });
    trackAnalyticsEventAPI({
      eventName: 'favorite_toggle',
      feature: 'favorites',
      metadata: { mode: entry.mode || 'unknown' },
    });
  }, [favorites]);

  const handleClearCache = async () => {
    setServerCacheFeedback(null);
    try {
      await clearServerCacheAPI();
      // Same clean slate as "Check another product" — hide results, clear inputs, reset hooks.
      resetAll();
      setLastManualInput('');
      setLastProductInput('');
      setLastScannerInput('');
      setOpenAllergens(new Set());
      setServerCacheFeedback({
        variant: 'success',
        message: 'Cache cleared. Your next search will use fresh data.',
      });
      window.setTimeout(() => setServerCacheFeedback(null), 3000);
    } catch (e) {
      // Never show raw API / server text to end users — they can’t fix infrastructure.
      const code = e?.code || e?.message;
      let message = 'Something went wrong. Please try again in a moment.';
      if (code === 'CACHE_CLEAR_FORBIDDEN') {
        message = 'That refresh isn’t available. You can still run a new search.';
      } else if (code === 'NETWORK') {
        message = 'Couldn’t connect. Check your internet and try again.';
      }
      setServerCacheFeedback({ variant: 'error', message });
      window.setTimeout(() => setServerCacheFeedback(null), 9000);
    }
  };

  const handleAccountExport = React.useCallback(async () => {
    const token = account.session?.access_token;
    if (!token) throw new Error('Sign in before exporting account data.');
    const data = await getAccountExportAPI(token);
    const blob = new Blob([`${JSON.stringify(data, null, 2)}\n`], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `vegai-data-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [account.session]);

  const handleAccountDelete = React.useCallback(async () => {
    const token = account.session?.access_token;
    if (!token) throw new Error('Sign in before deleting an account.');
    await deleteAccountAPI(token);
    // The Auth user is already gone, so Supabase may reject the follow-up
    // logout request. Deletion success remains authoritative; clear locally.
    await account.signOut({ ignoreRemoteError: true });
    setAccountOpen(false);
  }, [account]);

  // Wrapped analyze functions to capture input and clear other results
  const onManualAnalyze = (input) => {
    setLastManualInput(input);
    setOpenAllergens(new Set());
    setManualResult(null);
    setManualError(null);
    setProductResult(null);
    setProductError(null);
    setScannerResult(null);
    setScannerError(null);
    setProductResetKey(k => k + 1);
    setScannerResetKey(k => k + 1);
    return manual.analyze(input);
  };

  const onProductAnalyze = (input) => {
    setLastProductInput(input);
    setLastProductVariantLookup(null);
    setOpenAllergens(new Set());
    setProductResult(null);
    setProductError(null);
    setManualResult(null);
    setManualError(null);
    setScannerResult(null);
    setScannerError(null);
    setManualResetKey(k => k + 1);
    setScannerResetKey(k => k + 1);
    return product.analyze(input);
  };

  const onScannerAnalyze = (input) => {
    setLastScannerInput(input);
    setOpenAllergens(new Set());
    setScannerResult(null);
    setScannerError(null);
    setManualResult(null);
    setManualError(null);
    setProductResult(null);
    setProductError(null);
    setManualResetKey(k => k + 1);
    setProductResetKey(k => k + 1);
    return scanner.analyze(input);
  };

  // normalizeStatus is defined outside the component (module-level pure function)

  const formatRecommendationText = (rec, result) => {
    // Keep the existing special-case guidance for vegan results with only questionable ingredients.
    if (
      (result.status === 'vegan' || result.status === 'Vegan' || result.status === 'Plant-Based') &&
      result.questionableIngredients && result.questionableIngredients.length > 0 &&
      result.nonPlantBasedIngredients && result.nonPlantBasedIngredients.length === 0
    ) {
      return 'Everything looks vegan, though one ingredient is a little unclear. You are doing great - if you want extra certainty, do a quick manufacturer check.';
    }

    const cleaned = normalizeShoutingInText(String(rec || '').trim().replace(/\s+/g, ' '));
    if (!cleaned) return '';
    if (/[.!?]$/.test(cleaned)) return cleaned;
    return `${cleaned}.`;
  };

  // extractDetectedAllergens is defined outside the component (module-level pure function)

  // Handler for rerunning from history
  const handleRerunFromHistory = async (entry) => {
    trackAnalyticsEventAPI({
      eventName: 'history_rerun',
      feature: 'history',
      metadata: { mode: entry.mode || 'unknown', isRerun: true },
    });
    setIsHistoryHidden(true);
    rerunCancelledRef.current = false;
    rerunEntryRef.current = entry;
    setIsRerunning(true);
    setRerunId(entry.id);
    
    // Clear ALL input text boxes
    setManualInput('');
    setProductInput('');
    setScannerInput('');
    
    // Reset ALL modes so stale hook results don't resurface after rerunId clears
    resetManual();
    resetProduct();
    resetScanner();

    // Clear lastInput states to prevent history capture effects from firing incorrectly
    setLastManualInput('');
    setLastProductInput('');
    setLastScannerInput('');
    
    const startTime = Date.now();

    /** Use the promise return value — hook `.result` here is a stale closure after `await`. */
    let rerunOutcome = undefined;
    let rerunError = null;
    try {
      if (entry.mode === 'product') {
        // If the original search selected an exact DB record (fdcId/offCode), look it up
        // directly instead of re-searching by label — avoids a different product match.
        if (entry.variantLookup && (entry.variantLookup.fdcId != null || entry.variantLookup.offCode != null)) {
          setLastProductInput(entry.input);
          setLastProductVariantLookup(entry.variantLookup);
          rerunOutcome = await product.analyzeVariant(entry.variantLookup);
        } else {
          const rerunProductInput = buildContextualProductInput({
            label: entry.input,
            productContext: entry.productMatch || entry.historyLabel,
          });
          setLastProductInput(rerunProductInput);
          rerunOutcome = await onProductAnalyze(rerunProductInput);
        }
      } else if (entry.mode === 'manual') {
        rerunOutcome = await onManualAnalyze(entry.input);
      } else {
        rerunOutcome = await onScannerAnalyze(entry.rerunInput || entry.input);
      }
    } catch (err) {
      // Surface the error rather than letting it propagate as an unhandled
      // promise rejection. The mode-specific error effect will pick it up
      // once rerunId clears below.
      rerunError = err && err.message ? err.message : 'Failed to re-run that analysis.';
    } finally {
      // If user cancelled, don't set any results
      if (rerunCancelledRef.current) {
        rerunEntryRef.current = null;
        return;
      }

      // Ensure minimum 2 seconds of loading time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 2000 - elapsedTime);

      if (remainingTime > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingTime));
      }

      // Check again after the delay in case user cancelled during the wait
      if (rerunCancelledRef.current) {
        rerunEntryRef.current = null;
        return;
      }

      // Clear lastInput BEFORE clearing rerunId so the history-capture effects
      // don't re-fire and append a duplicate entry for the original row.
      setLastManualInput('');
      setLastProductInput('');
      setLastScannerInput('');

      // Clear rerun state to allow results to display
      setIsRerunning(false);
      setRerunId(null);
      rerunEntryRef.current = null;

      if (rerunOutcome != null) {
        if (entry.mode === 'manual') setManualResult(rerunOutcome);
        else if (entry.mode === 'product') setProductResult(rerunOutcome);
        else setScannerResult(rerunOutcome);
      } else if (rerunError) {
        // No outcome and no cancellation → show the error in the original mode's panel.
        if (entry.mode === 'manual') setManualError(rerunError);
        else if (entry.mode === 'product') setProductError(rerunError);
        else setScannerError(rerunError);
      }
    }
  };

  // Function to improve summary text to avoid misleading "animal-derived" language
  const getImprovedSummary = (activeResult) => {
    if (!activeResult.summary) return '';
    
    // Determine status from both legacy isPlantBased field and new status field
    const status = activeResult.status || '';
    const isVegan = status === 'vegan' || status === 'Plant-Based' || activeResult.isPlantBased === true;
    const isNotVegan = status === 'not-vegan' || status === 'Not Plant-Based' || activeResult.isPlantBased === false;
    const isMaybe = status === 'maybe-vegan' || status === 'Maybe Plant-Based' || activeResult.isPlantBased === "Possibly Not Plant-Based";
    
    // Handle "maybe-vegan" case - show questionable ingredients
    if (isMaybe || (isNotVegan && activeResult.confidence < 100)) {
      if (activeResult.questionableIngredients && activeResult.questionableIngredients.length > 0) {
        return "Some ingredients are unclear or hard to verify. Check the questionable ingredients listed below.";
      }
      return "Some ingredients are questionable and may not be vegan.";
    }
    
    // If it's definitely not vegan, show non-vegan ingredients
    if (isNotVegan && activeResult.confidence === 100) {
      if (activeResult.nonPlantBasedIngredients && activeResult.nonPlantBasedIngredients.length > 0) {
        return "This product contains ingredients that are not vegan.";
      }
    }
    
    // If it's vegan, use the summary (normalize any ALL CAPS product names)
    if (isVegan) {
      return normalizeShoutingInText(activeResult.summary);
    }
    
    // Check if the summary contains misleading "animal-derived" language
    const animalKeywords = ['animal', 'meat', 'dairy', 'egg', 'milk', 'beef', 'pork', 'chicken', 'fish', 'honey', 'gelatin'];
    const hasAnimalContent = animalKeywords.some(keyword => 
      activeResult.summary.toLowerCase().includes(keyword.toLowerCase())
    );
    
    if (hasAnimalContent) {
      return normalizeShoutingInText(activeResult.summary);
    }
    
    // If summary contains "animal-derived" but no actual animal ingredients, replace it
    if (activeResult.summary.toLowerCase().includes('animal-derived') && !hasAnimalContent) {
      if (activeResult.problematicIngredients && activeResult.problematicIngredients.length > 0) {
        const ingredientList = activeResult.problematicIngredients.join(', ');
        return `The product is not entirely vegan due to the presence of ${ingredientList}.`;
      }
    }
    
    // Otherwise, use more general wording
    if (activeResult.problematicIngredients && activeResult.problematicIngredients.length > 0) {
      const ingredientList = activeResult.problematicIngredients.join(', ');
      return `The product is not entirely vegan due to the presence of ${ingredientList}.`;
    }
    
    // Fallback to original summary if no problematic ingredients found
    return normalizeShoutingInText(activeResult.summary);
  };


  // --- LOCKED ALLERGY NOTICE FUNCTION ---
  const renderAllergyNotice = (result, openAllergens, toggleAllergen) => {
    const allergens = result && result.allergens ? result.allergens : [];
    const glutenGrains = result && result.glutenGrains ? result.glutenGrains : [];
    const hasAllergens = allergens.length > 0;
    const hasGlutenGrains = glutenGrains.length > 0;
    const advisoryPattern = /\bmay\s+(?:also\s+)?contain\b|\btrace(?:s)?\s+of\b|\bshared\s+equipment\b|\bcross[-\s]*(?:contact|contamination)\b|\bprocessed\s+in\s+(?:a\s+)?facility\b/;
    const allergyRows = allergens.map((allergen, idx) => {
      const allergenName = allergen.allergen ? formatProductLabel(allergen.allergen) : '';
      const advisoryText = `${allergen.note || ''} ${allergen.source || ''}`.toLowerCase();
      const isAdvisory = Boolean(allergen.advisory) || advisoryPattern.test(advisoryText);
      const isOpen = openAllergens.has(allergen.allergen?.toLowerCase());

      let description = '';
      if (ALLERGEN_DESCRIPTIONS[allergen.allergen?.toLowerCase()]) {
        description = ALLERGEN_DESCRIPTIONS[allergen.allergen.toLowerCase()];
      } else if (allergen.note) {
        description = allergen.note.charAt(0).toUpperCase() + allergen.note.slice(1);
        if (!/[.!?]$/.test(description)) description += '.';
      } else {
        description = `${allergenName} may cause allergic reactions in some individuals.`;
      }

      return {
        allergen,
        allergenName,
        description,
        displayName: allergenName,
        isAdvisory,
        isOpen,
        key: `${allergen.allergen || 'allergen'}-${idx}`,
      };
    });
    const directAllergens = allergyRows.filter((row) => !row.isAdvisory);
    const advisoryAllergens = allergyRows.filter((row) => row.isAdvisory);

    const renderAllergenRows = (rows) => (
      <ul className="list-disc ml-6 space-y-2">
        {rows.map((row) => (
          <li key={row.key} className="flex flex-col gap-1 relative">
            <div className="flex items-center gap-2">
              <span className="font-bold text-blue-800 text-base">{row.displayName}</span>
              <button
                type="button"
                onClick={() => toggleAllergen(row.allergen.allergen?.toLowerCase())}
                className="text-gray-600 hover:text-gray-800 cursor-pointer text-base transition-colors focus:outline-none bg-transparent border-none p-0 m-0"
                aria-label="Show allergen info"
              >
                <span role="img" aria-label="info" className="text-lg align-middle">ⓘ</span>
              </button>
            </div>
            {row.isOpen && (
              <div className="pt-2 pb-2 px-0 text-base text-blue-800 font-normal leading-relaxed text-left w-full bg-blue-100">
                <p className="m-0">{row.description}</p>
              </div>
            )}
          </li>
        ))}
      </ul>
    );

    return (
      <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 mb-4">
        <h4 className="text-blue-800 font-bold text-lg mb-3">Allergy Notice</h4>
        {hasAllergens ? (
          <div className="space-y-4">
            {directAllergens.length > 0 && (
              <div>
                <h5 className="text-blue-800 font-bold text-base mb-2">Detected allergens</h5>
                {renderAllergenRows(directAllergens)}
              </div>
            )}
            {advisoryAllergens.length > 0 && (
              <div>
                <h5 className="text-blue-800 font-bold text-base mb-2">May contain</h5>
                {renderAllergenRows(advisoryAllergens)}
              </div>
            )}
          </div>
        ) : (
          !hasGlutenGrains && (
            <div className="text-blue-800 mb-2">
              No major allergens were detected. Other sensitivities are still possible, so if you have concerns, please double-check.
            </div>
          )
        )}

        {hasGlutenGrains && (
          <div className={hasAllergens ? 'mt-4' : ''}>
            <h5 className="text-blue-800 font-bold text-base mb-3">Gluten-Containing Grains</h5>
            <ul className="list-disc ml-6 space-y-2">
              {glutenGrains.map((item, idx) => {
                const grainKey = item.grain?.toLowerCase();
                const grainLabel = formatProductLabel(item.grain || '');
                const isOpen = openAllergens.has(`gluten-${grainKey}`);
                const description = ALLERGEN_DESCRIPTIONS[grainKey] || `${grainLabel} contains gluten. People with celiac disease or gluten sensitivity should avoid it.`;

                return (
                  <li key={idx} className="flex flex-col gap-1 relative">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-blue-800 text-base">{grainLabel}</span>
                      <button
                        type="button"
                        onClick={() => toggleAllergen(`gluten-${grainKey}`)}
                        className="text-gray-600 hover:text-gray-800 cursor-pointer text-base transition-colors focus:outline-none bg-transparent border-none p-0 m-0"
                        aria-label="Show gluten grain info"
                      >
                        <span role="img" aria-label="info" className="text-lg align-middle">ⓘ</span>
                      </button>
                    </div>
                    {isOpen && (
                      <div className="pt-2 pb-2 px-0 text-base text-blue-800 font-normal leading-relaxed text-left w-full bg-blue-100">
                        <p className="m-0">{description}</p>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {!hasAllergens && !hasGlutenGrains && null}
      </div>
    );
  };
  // --- END LOCKED ALLERGY NOTICE FUNCTION ---

  const renderScan = () => {
    const hasResultsPanel = !!(
      manualResult ||
      productResult ||
      scannerResult ||
      isAnalyzingManual ||
      isAnalyzingProduct ||
      isAnalyzingScanner
    );
    const isAnalyzing = isAnalyzingManual || isAnalyzingProduct || isAnalyzingScanner;
    const activeResult = manualResult || productResult || scannerResult;
    const hasCompletedResult = !!(activeResult && !isAnalyzing);
    const mappedStatus = activeResult?.status ? mapStatusToDisplay(activeResult.status) : null;
    const hasForYouMatches = (
      (Array.isArray(activeResult?.forYou?.matches) && activeResult.forYou.matches.length > 0) ||
      (Array.isArray(activeResult?.forYou?.groups) && activeResult.forYou.groups.length > 0)
    );
    const hasReferenceCards = !!(
      activeResult &&
      (
        activeResult.recommendations?.length > 0 ||
        (productResult && activeResult === productResult) ||
        activeResult.disclaimer === true
      )
    );

    const renderResultsHeader = () => (
      <>
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl font-black tracking-tight text-slate-900">Analysis Result</h2>
          {(manualResult || productResult || scannerResult) && (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSaveFavorite}
                aria-pressed={isActiveFavoriteSaved}
                aria-label={isActiveFavoriteSaved ? 'Remove result from favorites' : 'Save result to favorites'}
                title={isActiveFavoriteSaved ? 'Remove from favorites' : 'Save to favorites'}
                className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-bold transition-colors ${
                  isActiveFavoriteSaved
                    ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
                    : 'border-rose-200 bg-white text-rose-700 hover:bg-rose-50'
                }`}
              >
                <Heart className={`h-4 w-4 ${isActiveFavoriteSaved ? 'fill-emerald-600 text-emerald-600' : ''}`} />
                {isActiveFavoriteSaved ? 'Saved' : 'Save'}
              </button>
              <button
                onClick={resetAll}
                className="text-sm font-medium text-emerald-700 hover:text-emerald-900 underline"
              >
                Check another product
              </button>
            </div>
          )}
        </div>
        {favoriteFeedback && (
          <p
            className={`mb-3 text-sm ${favoriteFeedback.type === 'success' ? 'text-emerald-700' : 'text-red-600'}`}
            role="status"
          >
            {favoriteFeedback.text}
          </p>
        )}
      </>
    );

    const renderMatchedProduct = () => {
      if (!((productResult && productResult.productMatch) ||
        (scannerResult && scannerResult.productMatch && !scannerResult.scannedIngredients?.length))) {
        return null;
      }

      return (
        <div className="mb-4 flex items-center rounded-2xl border border-[#ead681] bg-[#fff9d9] px-4 py-3 text-base font-semibold text-[#80631a]">
          <span className="mr-2">🔎</span>
          <span>
            Matched Product:{' '}
            {formatProductLabel(
              ((productResult || scannerResult).productMatch || '').replace(/[-_]+/g, ' ')
            )}
          </span>
        </div>
      );
    };

    const renderLoadingState = () => (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Loader2 className="w-14 h-14 text-emerald-600 animate-spin mb-4" />
        <p className="text-lg text-slate-800 font-bold mb-1">
          {isAnalyzingProduct ? 'Analyzing product...' : isAnalyzingScanner ? 'Processing scan...' : 'Analyzing your ingredients…'}
        </p>
        <p className="text-sm text-slate-500 mb-3">This usually takes a few seconds. Tiny veggie robots are squinting.</p>
        {(isAnalyzingProduct || isAnalyzingManual || isAnalyzingScanner) && (
          <div className="max-w-xs rounded-2xl bg-emerald-50 px-4 py-3 text-center text-xs text-emerald-800">
            Searching for ingredients...<br/>
            AI is classifying ingredients...<br/>
            Applying safety overrides...
          </div>
        )}
      </div>
    );

    const renderVerdictCard = () => {
      if (!activeResult || !activeResult.status) return null;

      if (mappedStatus === "Not Vegan") {
        return (
          <div className="mb-4 flex w-full flex-col items-center rounded-[1.75rem] border border-red-200 bg-red-50 p-6 text-center">
            <div className="w-12 h-12 text-4xl mb-2 flex items-center justify-center">❌</div>
            <h3 className="text-3xl font-black tracking-tight text-red-900">{mappedStatus}</h3>
            <p className="mt-1 text-sm font-medium text-red-700">This product contains non vegan ingredients.</p>
            {activeResult.nonPlantBasedIngredients && activeResult.nonPlantBasedIngredients.length > 0 && (
              <div className="w-full mt-4">
                <h4 className="text-base font-semibold text-red-800 mb-2 text-left">Not vegan:</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-700 text-left text-base">
                  {activeResult.nonPlantBasedIngredients.map((ingredient, idx) => {
                    const note = getVitaminNote(ingredient, "nonPlantBasedIngredients");
                    return (
                      <li key={idx}>
                        {formatIngredientDisplay(ingredient)}
                        {note && (
                          <span title={note}>
                            <Info className="inline-block w-4 h-4 text-gray-500 ml-1" />
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        );
      } else if (mappedStatus === "Possibly Not Vegan") {
        return (
          <div className="mb-4 flex w-full flex-col items-center rounded-[1.75rem] border border-amber-200 bg-amber-50 p-6 text-center">
            <div className="w-12 h-12 text-4xl mb-2 flex items-center justify-center">⚠️</div>
            <h3 className="text-3xl font-black tracking-tight text-amber-950">{mappedStatus}</h3>
            <p className="mt-2 rounded-full bg-white/70 px-3 py-1 text-sm font-bold text-amber-700">Confidence: {activeResult.confidence}%</p>
            <div className="mt-3 text-slate-700 leading-relaxed text-center">
              Some ingredients are questionable and may not be vegan. Please review the list below.
            </div>
          </div>
        );
      } else if (mappedStatus === "Vegan") {
        return (
          <div className="mb-4 flex flex-col items-center rounded-[2rem] border border-emerald-200 bg-emerald-50/90 p-6 text-center">
            <div className="w-12 h-12 text-4xl mb-2 flex items-center justify-center">🌱</div>
            <h3 className="text-4xl font-black tracking-tight text-emerald-900">{mappedStatus}</h3>
            <p className="mt-2 rounded-full bg-white/70 px-3 py-1 text-sm font-bold text-emerald-700">{activeResult.confidence}% confidence</p>
            {(() => {
              if (activeResult.confidence !== 100) return null;
              if (activeResult.questionableIngredients?.length > 0) return null;
              if (activeResult.nonPlantBasedIngredients?.length > 0) return null;

              const isProductMode = !!productResult;
              if (isProductMode) {
                const name = (activeResult.productMatch || '').toLowerCase();
                const isExplicitlyVegan = /\bvegan\b|plant[- ]based/.test(name);
                if (!isExplicitlyVegan) return null;
              }

              return (
                <div className="mt-3 flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white/75 px-3 py-1">
                  <span className="text-sm font-semibold text-emerald-800">VegAI Confirmed</span>
                  <svg className="w-4 h-4 text-green-700 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              );
            })()}
            <div className="mt-3 text-slate-700 leading-relaxed text-center">{getImprovedSummary(activeResult)}</div>
            {supportiveMessage && activeResult && !isAnalyzingProduct && (
              <div className="mt-4 text-green-700 text-base font-medium text-center">
                {supportiveMessage}
              </div>
            )}
          </div>
        );
      }

      // Fallback for any unrecognised status — render the mapped string if we
      // have one, otherwise default to "Possibly Not Vegan" so the user always
      // sees a result.
      const headerText = mappedStatus || "Possibly Not Vegan";
      return (
        <div className="bg-yellow-100 border-2 border-yellow-300 flex flex-col items-center p-4 rounded-xl mb-4 w-full">
          <div className="w-12 h-12 text-4xl mb-2 flex items-center justify-center">⚠️</div>
          <h3 className="text-lg font-semibold text-yellow-800">{headerText}</h3>
          <p className="text-sm text-yellow-600">Confidence: {activeResult.confidence}%</p>
          <div className="mt-2 text-gray-700 leading-relaxed text-center">
            Some ingredients are unclear or hard to verify. Check the questionable ingredients listed below.
          </div>
        </div>
      );
    };

    const renderFlagReview = () => (
      <>
        <div className="mt-4 mb-4 rounded-xl border border-[#a8d4cc] bg-white px-4 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-[#3d5f56]">
              Does this result feel a little… overripe? Flag it for us.
            </p>
            <button
              type="button"
              onClick={openFlagReviewDialog}
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-lg border border-[#8fb8b0] bg-white px-3 py-2 text-sm font-semibold text-[#3d5f56] shadow-sm hover:bg-[#e8f4f2]"
            >
              <Flag className="h-4 w-4" aria-hidden />
              Check freshness
            </button>
          </div>
          {flagReviewFeedback && (!flagReviewDialogOpen || flagReviewFeedback.type === 'success') && (
            <p
              className={`mt-2 text-sm ${flagReviewFeedback.type === 'success' ? 'text-emerald-700' : 'text-red-600'}`}
              role="status"
            >
              {flagReviewFeedback.text}
            </p>
          )}
        </div>
        {flagReviewDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6">
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="flag-review-title"
              className="w-full max-w-md rounded-2xl border border-rose-200 bg-white p-5 text-left shadow-2xl"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 id="flag-review-title" className="text-lg font-bold text-rose-950">
                    Freshness check
                  </h3>
                  <p className="mt-1 text-sm text-rose-900/75">
                    Choose what feels past its prime.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeFlagReviewDialog}
                  className="rounded-full px-2 py-1 text-xl leading-none text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Close issue report dialog"
                >
                  ×
                </button>
              </div>

              <label className="mt-5 block text-xs font-semibold uppercase tracking-wide text-rose-900/70">
                Reason
                <select
                  value={flagReviewReason}
                  onChange={(event) => {
                    setFlagReviewReason(event.target.value);
                    setFlagReviewFeedback(null);
                  }}
                  className="mt-1 w-full rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm normal-case tracking-normal text-rose-950 shadow-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
                >
                  <option value="">Select reason</option>
                  {FLAG_REVIEW_REASONS.map((reason) => (
                    <option key={reason.value} value={reason.value}>{reason.label}</option>
                  ))}
                </select>
              </label>

              {flagReviewReason === 'other' ? (
                <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-rose-900/70">
                  Details
                  <input
                    type="text"
                    value={flagReviewOtherText}
                    onChange={(event) => {
                      setFlagReviewOtherText(event.target.value);
                      setFlagReviewFeedback(null);
                    }}
                    maxLength={300}
                    placeholder="What should we review?"
                    className="mt-1 w-full rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm normal-case tracking-normal text-rose-950 shadow-sm focus:border-rose-400 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  />
                </label>
              ) : null}

              {flagReviewFeedback?.type === 'error' && (
                <p className="mt-3 text-sm text-red-600" role="status">
                  {flagReviewFeedback.text}
                </p>
              )}

              <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeFlagReviewDialog}
                  className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleFlagForReview}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-300 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-900 shadow-sm hover:bg-rose-100"
                >
                  <Flag className="h-4 w-4" aria-hidden />
                  Send report
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );

    const renderScannedIngredients = ({ compact = false } = {}) => {
      const scannedIngredients = Array.isArray(activeResult?.scannedIngredients)
        ? activeResult.scannedIngredients.map((ingredient) => String(ingredient || '').trim()).filter(Boolean)
        : [];
      if (scannedIngredients.length === 0) return null;

      if (!compact) {
        return (
          <div className="mt-4 mb-4 rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4 text-left">
            <h3 className="font-black mb-1.5 text-lg text-orange-700">Scanned ingredients</h3>
            <p className="text-sm text-amber-950 mb-3">
              VegAI read these ingredients from the label. Review them to make sure the scan looks right.
            </p>
            <div className="flex flex-wrap gap-2">
              {scannedIngredients.map((ingredient, idx) => (
                <span
                  key={`${ingredient}-${idx}`}
                  className="vegai-chip"
                >
                  {formatIngredientDisplay(ingredient)}
                </span>
              ))}
            </div>
          </div>
        );
      }

      const previewIngredients = scannedIngredients.slice(0, 3);
      const extraCount = Math.max(scannedIngredients.length - previewIngredients.length, 0);

      return (
        <>
          <button
            type="button"
            onClick={() => setIsScannedIngredientsDialogOpen(true)}
            className="mt-4 mb-4 w-full rounded-[1.5rem] border border-amber-200 bg-amber-50 p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-amber-300 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300"
            aria-haspopup="dialog"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-black mb-1 text-lg text-orange-700">Scanned ingredients</h3>
                <p className="text-sm text-amber-950">
                  VegAI read {scannedIngredients.length} ingredient{scannedIngredients.length === 1 ? '' : 's'} from the label.
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-white/80 px-3 py-1 text-xs font-bold text-orange-700">
                View all
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {previewIngredients.map((ingredient, idx) => (
                <span
                  key={`${ingredient}-${idx}`}
                  className="vegai-chip"
                >
                  {formatIngredientDisplay(ingredient)}
                </span>
              ))}
              {extraCount > 0 && (
                <span className="vegai-chip bg-white/80 text-orange-800">
                  +{extraCount} more
                </span>
              )}
            </div>
            <p className="mt-3 text-xs font-medium text-amber-900">
              Tap to review the full list before trusting the scan.
            </p>
          </button>

          {isScannedIngredientsDialogOpen && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 py-6"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setIsScannedIngredientsDialogOpen(false);
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="scanned-ingredients-dialog-title"
                className="max-h-[85vh] w-full max-w-lg overflow-hidden rounded-[1.75rem] border border-amber-200 bg-amber-50 shadow-2xl"
              >
                <div className="flex items-start justify-between gap-4 border-b border-amber-200 bg-amber-50 px-5 py-4">
                  <div>
                    <h3 id="scanned-ingredients-dialog-title" className="text-xl font-black text-orange-700">
                      Scanned ingredients
                    </h3>
                    <p className="mt-1 text-sm text-amber-950">
                      Review what VegAI read from the label. If something looks off, rescan or paste the list manually.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsScannedIngredientsDialogOpen(false)}
                    className="rounded-full border border-amber-200 bg-white px-3 py-1 text-lg font-black leading-none text-orange-700 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-300"
                    aria-label="Close scanned ingredients"
                  >
                    ×
                  </button>
                </div>
                <div className="max-h-[55vh] overflow-y-auto bg-amber-50 px-5 py-4">
                  <ul className="list-disc space-y-2 pl-5 text-sm font-medium text-slate-800 marker:text-orange-500">
                    {scannedIngredients.map((ingredient, idx) => (
                      <li
                        key={`${ingredient}-${idx}`}
                        className="pl-1 leading-relaxed"
                      >
                        {formatIngredientDisplay(ingredient)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </>
      );
    };

    const renderQuestionableIngredients = () => {
      if (!activeResult?.questionableIngredients?.length) return null;

      return (
        <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mt-6">
          <h3 className="font-semibold mb-2 text-lg" style={{ color: '#000' }}>Questionable Ingredients</h3>
          <ul className="list-disc pl-6 space-y-1 text-gray-700 text-left text-base">
            {activeResult.questionableIngredients.map((ingredient, idx) => (
              <li key={idx}>
                {formatIngredientDisplay(ingredient)}
              </li>
            ))}
          </ul>
          <div className="mt-4 text-sm italic text-gray-600">
            These ingredients are usually context-dependent. Some are commonly vegan in packaged foods, but source details are not always shown on labels. For extra certainty, verify with the manufacturer or choose certified vegan products.
          </div>
        </div>
      );
    };

    const renderRecommendations = () => {
      if (!activeResult?.recommendations?.length) return null;

      return (
        <div className="mt-6 mb-6 bg-purple-100 border border-purple-300 rounded-lg p-4">
          <h3 className="font-semibold text-purple-800 mb-3 flex items-center text-lg">
            Recommendations
          </h3>
          {(() => {
            // De-duplicate after formatting: the vegan-with-questionable path
            // collapses every input recommendation to the same "Everything
            // looks vegan..." sentence, so without a dedup pass we render the
            // identical line two or three times.
            const seen = new Set();
            const formattedRecommendations = [];
            for (const rec of activeResult.recommendations) {
              const formatted = formatRecommendationText(rec, activeResult);
              if (!formatted) continue;
              const key = formatted.toLowerCase();
              if (seen.has(key)) continue;
              seen.add(key);
              formattedRecommendations.push(formatted);
              if (formattedRecommendations.length >= 2) break;
            }

            if (formattedRecommendations.length === 1) {
              return <div className="text-purple-700 text-base">{formattedRecommendations[0]}</div>;
            }

            return (
              <ul className="list-disc ml-6 space-y-2 text-base">
                {formattedRecommendations.map((rec, idx) => (
                  <li key={idx} className="text-purple-700">
                    {rec}
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>
      );
    };

    const renderDataSource = () => {
      if (!(productResult && activeResult === productResult)) return null;

      const rawSources = Array.isArray(activeResult.sources) ? activeResult.sources : [];
      const isLLMOnly = activeResult.resolvedVia === 'llm';

      const getSourceBadge = (src) => {
        if (src.type === 'verified' || src.type === 'verified_product') {
          return { text: 'Verified' };
        }
        if (src.type === 'manufacturer' || src.type === 'smartlabel') {
          return { text: 'Official' };
        }
        if (src.type === 'usda' || (src.domain || '').includes('nal.usda.gov')) {
          return { text: 'USDA' };
        }
        if (src.type === 'off' || (src.domain || '').includes('openfoodfacts.org')) {
          return { text: 'Open Food Facts' };
        }
        return { text: src.domain || 'Web' };
      };

      const rows = rawSources.slice(0, 3).map((src) => {
        const url = String(src?.url || src?.link || src?.href || '').trim();
        const badge = getSourceBadge(src);
        const rawProductName = src?.name || (src?.title || '').replace(/^(USDA FoodData Central|Open Food Facts):\s*/i, '').trim();
        const productName = formatProductLabel(rawProductName);
        return { url, badge, productName, hasValidUrl: isValidHttpSourceUrl(url) };
      });

      return (
        <div className="mt-4 min-w-0 p-3 bg-[#f5efe6] border border-[#d4c4a8] rounded-lg">
          <p className="text-xs font-semibold text-[#6b5b3e] mb-2">Data Source</p>
          {isLLMOnly && (
            <p className="text-xs text-amber-700 mb-2">
              Ingredients were generated by AI because product databases were unavailable. Results may be less accurate — please verify on the product packaging.
            </p>
          )}
          {rows.length > 0 && (
            <ul className="space-y-2 min-w-0">
              {rows.map((src, i) => (
                <li key={i} className="flex min-w-0 items-start gap-2">
                  <span className="inline-block text-xs font-semibold px-1.5 py-0.5 rounded shrink-0 mt-0.5 bg-[#f5efe6] text-[#6b5b3e] border border-[#d4c4a8]">
                    {src.badge.text}
                  </span>
                  <div className="min-w-0 flex-1">
                    {src.hasValidUrl ? (
                      <a
                        href={src.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex min-w-0 w-full items-start gap-1 text-xs text-[#7d6b4f] hover:text-[#4a3d28] hover:underline"
                      >
                        <ExternalLink className="w-3 h-3 shrink-0 mt-0.5" />
                        <span className="min-w-0 flex-1 break-words">
                          {src.productName || 'View record'}
                        </span>
                      </a>
                    ) : (
                      <span className="block min-w-0 break-words text-xs text-[#7d6b4f]">
                        {src.productName || 'Verified database record'}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {rows.length === 0 && !isLLMOnly && (
            <p className="text-xs text-[#7d6b4f]">No external sources available</p>
          )}
        </div>
      );
    };

    const renderDisclaimer = () => {
      if (!(activeResult && activeResult.disclaimer === true)) return null;

      return (
        <div className="mt-4 p-3 bg-gray-100 border border-gray-300 rounded-lg text-xs text-gray-700">
          <b>Disclaimer:</b> This is an AI-generated analysis and is for informational purposes only. Results may not be 100% accurate. Always verify ingredients on product packaging and consult manufacturing processes if you have strict dietary requirements.
        </div>
      );
    };

    const renderReferenceCards = () => (
      <>
        {renderRecommendations()}
        {renderDataSource()}
        {renderDisclaimer()}
      </>
    );

    const renderMobileCompletedResult = () => (
      <div>
        {/* Main Result Card (Plant-Based, Not Plant-Based, etc.) */}
        {renderVerdictCard()}
        {renderFlagReview()}
        {renderScannedIngredients({ compact: false })}
        {/* Allergy Notice */}
        <ForYouCard data={activeResult.forYou} />
        {renderAllergyNotice(activeResult, openAllergens, toggleAllergen)}
        {/* Questionable Ingredients Section */}
        {renderQuestionableIngredients()}
        {/* Recommendations */}
        {renderReferenceCards()}
      </div>
    );

    const renderDesktopVerdictSafety = () => (
      <>
        {renderVerdictCard()}
        {renderScannedIngredients({ compact: usesDesktopResultZones })}
        {renderFlagReview()}
        {renderAllergyNotice(activeResult, openAllergens, toggleAllergen)}
        {renderQuestionableIngredients()}
      </>
    );

    return (
    <div className={`relative z-10 mx-auto pt-8 md:pt-8 ${hasResultsPanel ? 'max-w-[1180px]' : 'max-w-[1060px]'}`}>
      {/* Debug output for troubleshooting */}
      {/* <div style={{ background: '#f3f4f6', color: '#333', fontSize: '12px', padding: '8px', borderRadius: '6px', marginBottom: '12px' }}>
        <strong>DEBUG:</strong>
        <div>Ingredients: {JSON.stringify(ingredients)}</div>
        <div>Result: {JSON.stringify(result)}</div>
        <div>Error: {JSON.stringify(error)}</div>
      </div> */}
      <div
        className={
          hasResultsPanel
            ? // flex-col-reverse on mobile so the loading/results card appears
              // ABOVE the input panel — users no longer have to scroll past the
              // form to see the verdict. md+ uses two independent columns so
              // lower cards do not wait for the tallest card in the other column.
              'flex w-full flex-col-reverse gap-8 md:flex-row md:items-start md:justify-center lg:gap-10'
            : 'flex w-full flex-col items-center'
        }
      >
        {/* Ingredient Input Card (left on desktop; tips column follows on the right when idle) */}
        <div
          className={
            hasResultsPanel
              ? 'w-full md:flex md:w-[520px] md:max-w-[560px] md:shrink-0 md:flex-col md:gap-6'
              : 'w-full max-w-[900px] shrink-0'
          }
        >
          <div className={`vegai-glass-card space-y-6 p-5 md:p-8 lg:p-9 ${hasResultsPanel ? '' : 'vegai-scan-card vegai-scan-card--idle'}`}>
            {!hasResultsPanel && (
              <div className="text-center">
                <h1 className="vegai-hero-title text-4xl font-extrabold text-slate-950 md:text-5xl">
                  <span>Vegan check, simplified</span>
                </h1>
                <p className="mx-auto mt-3 max-w-lg text-lg leading-relaxed text-slate-700">
                  We’ll read the label and flag what matters.
                </p>
              </div>
            )}
            <div className="space-y-3">
              <Suspense
                fallback={
                  <div className="flex justify-center gap-3 py-10 text-emerald-800" aria-live="polite">
                    <Loader2 className="h-8 w-8 shrink-0 animate-spin" aria-hidden />
                    <span className="self-center text-sm font-medium">Loading scanner…</span>
                  </div>
                }
              >
                <ScannerInputPanel
                  onScan={onScannerAnalyze}
                  isLoading={scanner.isLoading}
                  error={scanner.error}
                  errorCode={scanner.errorCode}
                  errorContext={scanner.errorContext}
                  onReset={resetScanner}
                  onPivotToProductSearch={pivotToProductSearch}
                  resetKey={scannerResetKey}
                  pendingConfirmation={
                    scanner.pendingProduct?.needsConfirmation ? scanner.pendingProduct : null
                  }
                  onConfirmProduct={() => { scanner.confirmAndAnalyze(); }}
                />
              </Suspense>
            </div>

            <ProductInputPanel
              onAnalyze={onProductAnalyze}
              isLoading={product.isLoading}
              error={product.error}
              onReset={resetProduct}
              onCancel={cancelProduct}
              resetKey={productResetKey}
              inputRef={productInputRef}
              brands={product.brands}
              variants={product.variants}
              onSelectBrand={(brandObj) => {
                product.selectBrand(brandObj);
              }}
              onBackSelection={() => product.backFromProductSelection()}
              onSelectVariant={(variant) => {
                const label =
                  typeof variant === 'object' && variant !== null && variant.label != null
                    ? String(variant.label)
                    : String(variant);
                const ctx = {
                  productContext: product.pendingProduct?.originalInput,
                  brand: product.pendingProduct?.selectedBrand,
                };
                setLastProductInput(buildContextualProductInput({
                  label,
                  productContext: ctx.productContext,
                  brand: ctx.brand,
                }));
                // Store the full variant object so re-run can use the exact DB record.
                setLastProductVariantLookup(
                  typeof variant === 'object' && variant !== null &&
                  (variant.fdcId != null || variant.offCode != null)
                    ? variant
                    : null
                );
                setProductResult(null);
                setProductError(null);
                product.analyzeVariant(variant, ctx);
              }}
              onRefineProductSearch={(qualifier) => {
                const base = product.pendingProduct?.originalInput?.trim();
                const q = base ? `${base} ${qualifier}`.trim() : qualifier;
                setLastProductInput(q);
                setProductResult(null);
                setProductError(null);
                product.analyze(q);
              }}
              onCustomVariant={(custom) => {
                const ctx = {
                  productContext: product.pendingProduct?.originalInput,
                  brand: product.pendingProduct?.selectedBrand,
                };
                setLastProductInput(buildContextualProductInput({
                  label: custom,
                  productContext: ctx.productContext,
                  brand: ctx.brand,
                }));
                setLastProductVariantLookup(null);
                setProductResult(null);
                setProductError(null);
                product.analyzeVariant(custom, ctx);
              }}
              pendingConfirmation={
                product.pendingProduct?.needsConfirmation
                  ? product.pendingProduct
                  : null
              }
              onConfirmProduct={() => {
                product.confirmAndAnalyze();
              }}
              onClearCache={handleClearCache}
              serverCacheFeedback={serverCacheFeedback}
            />

            <div className="space-y-3">
              <ManualInputPanel
                onAnalyze={onManualAnalyze}
                isLoading={manual.isLoading}
                error={manual.error}
                resetKey={manualResetKey}
                onReset={resetManual}
              />
            </div>
          </div>
          {hasResultsPanel && usesDesktopResultZones && hasCompletedResult && hasForYouMatches && (
            <div className="vegai-glass-card w-full min-w-0 p-5 md:p-6">
              <ForYouCard data={activeResult.forYou} variant="desktop-quadrant" />
            </div>
          )}
        </div>
        {/* Results Output Card (Right on desktop, Top on mobile via flex-col-reverse) */}
        {hasResultsPanel && (
          <div ref={resultsPanelRef} className="w-full min-w-0 scroll-mt-4 md:min-w-[460px] md:flex-1">
            <div className="vegai-glass-card p-5 md:p-6">
              {renderResultsHeader()}
              {renderMatchedProduct()}
              {isAnalyzing ? renderLoadingState() : hasCompletedResult && (
                usesDesktopResultZones ? renderDesktopVerdictSafety() : renderMobileCompletedResult()
              )}
            </div>
            {usesDesktopResultZones && hasCompletedResult && hasReferenceCards && (
              <div className="vegai-glass-card mt-6 p-5 md:p-6">
                {renderReferenceCards()}
              </div>
            )}
          </div>
        )}
      </div>
      {showDesktopScrollHint && (
        <div className="fixed bottom-4 left-1/2 z-40 hidden -translate-x-1/2 rounded-full border border-slate-200 bg-white/90 px-4 py-2 text-sm font-semibold text-slate-500 shadow-lg backdrop-blur md:block">
          ↓ Scroll for more
        </div>
      )}
    </div>
    );
  };

  const feedbackContext = useMemo(
    () => {
      // Pick the most recently relevant result so the feedback dialog can
      // pre-populate the ingredient correction picker. Order: explicit product
      // search > scanner > manual paste — matching the user's likely focus.
      const liveResult = productResult || scannerResult || manualResult || null;
      const flagged = [
        ...(liveResult?.nonPlantBasedIngredients || []),
        ...(liveResult?.questionableIngredients || []),
      ]
        .map((s) => String(s || '').trim())
        .filter(Boolean)
        .slice(0, 20);

      return {
        activeTab,
        recentProduct:
          productResult?.productMatch ||
          scannerResult?.productMatch ||
          manualResult?.productMatch ||
          undefined,
        recentVerdict: liveResult?.status || undefined,
        flaggedIngredients: flagged,
        path:
          typeof window !== 'undefined'
            ? `${window.location.pathname}${window.location.search}`
            : undefined,
      };
    },
    [
      activeTab,
      productResult,
      scannerResult,
      manualResult,
    ]
  );

  if (wantsAdmin && !adminUiEnabled) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 text-slate-900">
        <div className="mx-auto max-w-lg rounded-2xl bg-white p-6 shadow-sm">
          <h1 className="text-xl font-bold text-emerald-800">Admin UI is off in this build</h1>
          <p className="mt-3 text-sm text-slate-600">
            The URL <code className="rounded bg-slate-100 px-1">#admin</code> is correct, but this
            frontend deploy was built without the admin flag. React bakes this in at{' '}
            <code className="rounded bg-slate-100 px-1">npm run build</code> time.
          </p>
          <p className="mt-3 text-sm font-semibold text-slate-800">In the Render frontend service, set:</p>
          <pre className="mt-2 overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
            REACT_APP_ENABLE_ADMIN_UI=true
          </pre>
          <p className="mt-3 text-sm text-slate-600">
            Redeploy the frontend after changing the environment variable, then open{' '}
            <code className="rounded bg-slate-100 px-1 break-all">
              {typeof window !== 'undefined' ? `${window.location.origin}/#admin` : '/#admin'}
            </code>{' '}
            again.
          </p>
        </div>
      </div>
    );
  }

  if (isAdminView) {
    return <AdminPanel />;
  }

  return (
    <div className="vegai-page-bg min-h-screen">
      <div className="vegai-produce-bg" aria-hidden="true">
        <svg className="vegai-produce-svg" viewBox="0 0 1200 720" preserveAspectRatio="xMidYMid slice" role="presentation">
          <defs>
            <radialGradient id="kiwiFlesh" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#f7fee7" />
              <stop offset="42%" stopColor="#bef264" />
              <stop offset="76%" stopColor="#84cc16" />
              <stop offset="100%" stopColor="#4d7c0f" />
            </radialGradient>
            <radialGradient id="citrusFlesh" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#fff7ed" />
              <stop offset="50%" stopColor="#fde68a" />
              <stop offset="100%" stopColor="#fb923c" />
            </radialGradient>
            <linearGradient id="carrotFill" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#fdba74" />
              <stop offset="55%" stopColor="#fb923c" />
              <stop offset="100%" stopColor="#f97316" />
            </linearGradient>
            <linearGradient id="leafFill" x1="0" x2="1" y1="0" y2="1">
              <stop offset="0%" stopColor="#bbf7d0" />
              <stop offset="100%" stopColor="#16a34a" />
            </linearGradient>
            <radialGradient id="tomatoFill" cx="35%" cy="28%" r="65%">
              <stop offset="0%" stopColor="#fecaca" />
              <stop offset="48%" stopColor="#f87171" />
              <stop offset="100%" stopColor="#ef4444" />
            </radialGradient>
          </defs>

          <g opacity="0.95">
            <ellipse cx="205" cy="505" rx="92" ry="158" fill="#d9f99d" opacity="0.30" transform="rotate(22 205 505)" />
            <ellipse cx="1015" cy="130" rx="100" ry="180" fill="#fed7aa" opacity="0.40" transform="rotate(-18 1015 130)" />
            <ellipse cx="1090" cy="380" rx="84" ry="190" fill="#facc15" opacity="0.36" transform="rotate(16 1090 380)" />
          </g>

          <g transform="translate(269 172)">
            <circle r="62" fill="url(#kiwiFlesh)" opacity="1" />
            <circle r="20" fill="#f7fee7" opacity="0.96" />
            <circle r="36" fill="none" stroke="#365314" strokeWidth="7" strokeDasharray="2 9" opacity="0.55" />
            <circle r="73" fill="none" stroke="#65a30d" strokeWidth="14" opacity="0.7" />
          </g>

          <g transform="translate(284 660)">
            <circle r="72" fill="url(#citrusFlesh)" opacity="0.94" />
            <circle r="36" fill="#fff7ed" opacity="0.55" />
            <path d="M-55 0H55M0-55V55M-39-39L39 39M39-39L-39 39" stroke="#fff7ed" strokeWidth="7" strokeLinecap="round" opacity="0.85" />
            <circle r="80" fill="none" stroke="#fb923c" strokeWidth="9" opacity="0.5" />
          </g>

          <g opacity="0.7">
            <path d="M255 112C292 61 344 47 365 54C330 106 280 126 255 112Z" fill="url(#leafFill)" />
            <path d="M305 420C360 368 427 364 453 378C393 430 331 446 305 420Z" fill="url(#leafFill)" opacity="0.85" />
            <path d="M265 520C322 495 378 515 398 540C332 560 286 552 265 520Z" fill="#86efac" opacity="0.75" />
          </g>

          <g transform="translate(1010 142) rotate(15)">
            <path d="M0 -118C48 -82 58 74 5 130C-46 72 -50 -86 0 -118Z" fill="url(#carrotFill)" opacity="0.96" />
            <path d="M-22 -78C-2 -68 18 -69 36 -78M-28 -34C-6 -24 20 -26 40 -38M-28 14C-7 24 20 22 39 10M-19 58C-2 70 17 68 32 56" fill="none" stroke="#c2410c" strokeWidth="3" strokeLinecap="round" opacity="0.5" />
            <path d="M-17 -125C-36 -167 -56 -174 -78 -180C-62 -142 -42 -125 -17 -125ZM3 -128C9 -171 30 -190 53 -202C50 -158 33 -132 3 -128ZM20 -118C58 -150 88 -145 108 -132C72 -107 45 -101 20 -118Z" fill="#65a30d" opacity="0.92" />
          </g>

          <g transform="translate(1089 324) rotate(12)" opacity="0.96">
            <path d="M-26 -160C56 -76 48 96 -38 162C18 56 19 -56 -26 -160Z" fill="#facc15" />
            <path d="M-26 -160C56 -76 48 96 -38 162C18 56 19 -56 -26 -160Z" fill="#fbbf24" opacity="0.5" />
            <path d="M-5 -148C53 -62 42 80 -32 145" fill="none" stroke="#fde68a" strokeWidth="20" strokeLinecap="round" opacity="0.7" />
            <path d="M-32 -156C-46 -178 -56 -188 -68 -190" fill="none" stroke="#a16207" strokeWidth="10" strokeLinecap="round" opacity="0.75" />
          </g>

          <g transform="translate(1006 628)">
            <circle r="68" fill="url(#tomatoFill)" opacity="0.86" />
            <circle cx="-22" cy="-24" r="14" fill="#fff7ed" opacity="0.5" />
            <path d="M-12 -72C-40 -118 -74 -130 -118 -128C-98 -85 -56 -62 -12 -72Z" fill="url(#leafFill)" opacity="0.85" />
            <path d="M40 -54C82 -94 124 -94 158 -80C118 -47 78 -38 40 -54Z" fill="url(#leafFill)" opacity="0.78" />
          </g>

          <g opacity="0.52" fill="#16a34a">
            <ellipse cx="255" cy="122" rx="8" ry="25" transform="rotate(33 255 122)" />
            <ellipse cx="284" cy="154" rx="8" ry="31" transform="rotate(64 284 154)" />
            <ellipse cx="320" cy="100" rx="7" ry="24" transform="rotate(28 320 100)" />
            <ellipse cx="1080" cy="78" rx="7" ry="24" transform="rotate(-24 1080 78)" />
            <ellipse cx="1128" cy="485" rx="9" ry="30" transform="rotate(50 1128 485)" />
          </g>

          <g fill="none" strokeLinecap="round" opacity="0.48">
            <path d="M265 86C288 71 313 69 338 79" stroke="#65a30d" strokeWidth="4" />
            <path d="M895 103C922 89 950 90 974 104" stroke="#65a30d" strokeWidth="4" />
            <path d="M938 197C915 214 901 235 896 263" stroke="#fb923c" strokeWidth="4" />
            <path d="M1116 265C1140 291 1144 318 1131 350" stroke="#fb923c" strokeWidth="4" />
            <path d="M282 646C305 666 331 670 360 660" stroke="#fb923c" strokeWidth="4" />
          </g>

          <g fill="#fb923c" opacity="0.56">
            <circle cx="284" cy="365" r="5" />
            <circle cx="1056" cy="206" r="5" />
            <circle cx="958" cy="58" r="6" />
            <circle cx="1150" cy="405" r="5" />
            <circle cx="320" cy="676" r="4" />
          </g>
        </svg>
      </div>
      {/* History Sidebar */}
      <HistorySidebar
        items={history.items}
        onRerun={handleRerunFromHistory}
        onRemove={(id) => history.remove(id)}
        onFavorite={handleFavoriteHistoryEntry}
        isFavorite={favorites.isSaved}
        onUpdateTitle={(id, title) => history.updateTitle(id, title)}
        onClear={() => history.clear()}
        rerunId={rerunId}
        hidden={isHistoryHidden}
        onToggleHidden={(hidden) => setIsHistoryHidden(hidden)}
      />

      {/* Rerun overlay */}
      {isRerunning && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl px-6 py-4 flex flex-col items-center gap-3">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/></svg>
              <span className="font-medium">Re-running analysis…</span>
            </div>
            <button
              onClick={cancelRerun}
              className="text-sm text-gray-500 hover:text-gray-800 underline"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <Header
        activeTab={activeTab}
        setActiveTab={handlePrimaryTabChange}
        historyHidden={isHistoryHidden}
        historyCount={history.items.length}
        onShowHistory={() => setIsHistoryHidden(false)}
        onHideHistory={() => setIsHistoryHidden(true)}
        accountUser={account.user}
        onOpenAccount={() => setAccountOpen(true)}
      />

      {/* Main content with left padding for sidebar */}
      <div className={`relative z-10 transition-all duration-200 ${isHistoryHidden ? 'md:pl-0' : 'md:pl-64'}`}>
        {/* Main Content */}
        <div className="p-4 pb-8">
          <div className={activeTab === 'scan' ? '' : 'hidden'}>{renderScan()}</div>
          {activeTab === 'you' && (
            <FoodProfilePanel foodProfile={foodProfile} signedIn={Boolean(account.user)} />
          )}
        </div>
        {/* Footer */}
        <div className="px-4 pb-8 text-center">
          <p className="text-emerald-800/70 text-sm font-medium">
            Clearer food labels, better choices 🍎
          </p>
          <div className="mx-auto mt-3 flex max-w-2xl flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm">
            <button
              type="button"
              onClick={() => setFavoritesOpen(true)}
              className="inline-flex items-center gap-1 font-semibold text-emerald-700 underline decoration-emerald-300 underline-offset-4 hover:text-emerald-900"
            >
              <Heart className="h-3.5 w-3.5" />
              Favorites
            </button>
            {[
              ['about', 'About'],
              ['privacy', 'Privacy'],
              ['terms', 'Terms'],
              ['guidance', 'Food guidance'],
              ['support', 'Support'],
            ].map(([page, label]) => (
              <button
                key={page}
                type="button"
                onClick={() => setLaunchInfoPage(page)}
                className="font-semibold text-emerald-700 underline decoration-emerald-300 underline-offset-4 hover:text-emerald-900"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <FavoritesModal
        open={favoritesOpen}
        items={favorites.items}
        onDismiss={() => setFavoritesOpen(false)}
        onRerun={handleRerunFromHistory}
        onRemove={favorites.remove}
        onClear={favorites.clear}
        storageDescription={
          account.user
            ? 'Favorites are synced to your VegAI account across signed-in devices.'
            : 'Favorites are saved in this browser on this device.'
        }
      />
      <AccountModal
        open={accountOpen}
        onDismiss={() => setAccountOpen(false)}
        account={account}
        syncLoading={accountData.loading}
        syncError={accountData.error}
        favoriteCount={favorites.items.length}
        historyCount={history.items.length}
        onExport={handleAccountExport}
        onDelete={handleAccountDelete}
      />
      <ImportLocalDataModal
        offer={combinedImportOffer}
        importing={accountData.importing || foodProfile.importing}
        error={accountData.error || foodProfile.error}
        onImport={handleImportDeviceData}
        onKeepSeparate={handleKeepDeviceDataSeparate}
      />
      {launchInfoPage && (
        <LaunchInfoModal
          page={launchInfoPage}
          onDismiss={() => setLaunchInfoPage(null)}
        />
      )}

      <FeedbackWidget context={feedbackContext} />
    </div>
  );
}
